import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { sendSupportEmail } from "@services/SendEmail";

interface ContactSupportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ContactSupportDialog: React.FC<ContactSupportDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [supportScreen, setSupportScreen] = useState("");
  const [supportDesc, setSupportDesc] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportMessage, setSupportMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleClose = () => {
    setSupportDesc("");
    setSupportScreen("");
    setSupportMessage(null);
    setSupportSubmitting(false);
    onClose();
  };

  const handleSend = async () => {
    try {
      setSupportSubmitting(true);
      const raw = localStorage.getItem("user");
      const user = raw ? JSON.parse(raw) : {};
      const name =
        user?.first_name || user?.firstName
          ? `${user.first_name || user.firstName} ${
              user.last_name || user.lastName || ""
            }`.trim()
          : user.display_name || user.displayName || "User";
      const email = user?.email || "";

      await sendSupportEmail({
        screen_name: supportScreen,
        description: supportDesc,
        user_name: name,
        user_email: email,
        user_number: "",
        to_email: "support@affablebpm.com",
        subject: `Support Request – ${supportScreen}`,
      });

      // Don't show success message in modal, only show toast
      // Call onSuccess callback to show toast
      if (onSuccess) {
        onSuccess();
      }
      // Close modal immediately after showing toast
      setTimeout(() => {
        handleClose();
      }, 300);
    } catch (e) {
      console.error("Support email failed", e);
      setSupportMessage({
        type: "error",
        text: "Failed to send. Please try again later.",
      });
    } finally {
      setSupportSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        },
      }}
      PaperProps={{
        sx: {
          width: "560px",
          height: "470px",
          borderRadius: "12px",
          border: "1px solid #CCF5FF",
          backgroundColor: "#fffdfd",
          boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
          "@media (max-width: 640px)": {
            width: "90vw",
            height: "80vh",
          },
        },
      }}
    >
      <DialogTitle sx={{ p: 2, borderBottom: "1px solid #BFE5EC" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "18px",
              color: "#065B70",
            }}
          >
            Contact Support
          </Typography>
          <Button
            onClick={handleClose}
            sx={{
              minWidth: 0,
              p: 0,
              color: "#065B70",
              textTransform: "none",
            }}
          >
            ✕
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 1,
          fontFamily: '"Rethink Sans", sans-serif',
        }}
      >
        {supportMessage && supportMessage.type === "error" && (
          <Box
            sx={{
              border: "1px solid #FCA5A5",
              background: "#FEF2F2",
              color: "#991B1B",
              borderRadius: "8px",
              px: 2,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: "12px" }}>
              {supportMessage.text}
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: "13px", color: "#737373" }}>
          We’re here to help you with any questions or issues
        </Typography>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontSize: "12px", color: "#737373", mb: "14px" }}>
            Select an area of the platform you are having issues with{" "}
            <span style={{ color: "#dc2626" }}>*</span>
          </Typography>
            <Select
            value={supportScreen}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: "#99A3A9" }}>Select an area...</span>;
              }
              return selected as string;
            }}
            onChange={(e) => setSupportScreen(e.target.value as string)}
            sx={{
              background: "#FDFFFF",
              border: "1px solid #99D4E1",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
            >
            <MenuItem value="" disabled>
              <em>Select an area...</em>
            </MenuItem>
            <MenuItem value="My Facility">My Facility</MenuItem>
            <MenuItem value="Regulations Library">Regulations Library</MenuItem>
            <MenuItem value="Discover">Discover</MenuItem>
            <MenuItem value="Regulations">Regulations</MenuItem>
            <MenuItem value="User Management">User Management</MenuItem>
            <MenuItem value="Pricing">Pricing</MenuItem>
            <MenuItem value="Billings">Payments & Billing</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: "12px", color: "#737373", mb: 0.5 }}>
          Description <span style={{ color: "#dc2626" }}>*</span>
        </Typography>
        <TextField
          placeholder="Describe the issue or question..."
          multiline
          minRows={4}
          value={supportDesc}
          onChange={(e) => setSupportDesc(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#99D4E1" },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#99D4E1",
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          sx={{
            background: "#FFFFFF",
            border: "1px solid #B3B3B3",
            borderRadius: "8px",
            px: 3,
            textTransform: "none",
            color: "#262626",
            "&:hover": { background: "#f5f5f5" },
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={!supportDesc.trim() || !supportScreen || supportSubmitting}
          onClick={handleSend}
          sx={{
            background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
            boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
            borderRadius: "8px",
            px: 3,
            textTransform: "none",
            color: "#e6fafc",
            "&:hover": {
              background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
            },
          }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};


